// Customizable Area Start
import { IBlock } from "../../../framework/src/IBlock";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { runEngine } from "../../../framework/src/RunEngine";
import emitter from "../../../components/src/eventBus.web";
import { IncomingCallSound } from "./assets";
export const configJSON = require("./config");
// Customizable Area End

// Customizable Area Start
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatCalls } from "@cometchat/calls-sdk-javascript";
import { CometCall } from "./ChatController";

const CALL_TYPES = {
  [CometChat.CALL_TYPE.VIDEO]: "Video Call",
  [CometChat.CALL_TYPE.AUDIO]: "Audio Call",
};

const COMETCHAT_LISTENER_ID = "COMETCHAT_LISTENER_ID";
const COMETCHAT_CALL_LISTENER_ID = "COMETCHAT_CALL_LISTENER_ID";
// Customizable Area End

export interface Props {
  // Customizable Area Start
  // Customizable Area End
}

interface S {
  // Customizable Area Start
  isFullScreen: boolean;
  isVideoCall: boolean;
  callerInfo: {
    name: string;
    avatar: string;
  };
  incomingCall: CometCall | null;
  isVideoOff: boolean;
  activeCallSession: CometCall | null;
  isMuted: boolean;
  callStatus: string;
  callTimer: number;
  isStatusError: boolean;
  isCallStarted: boolean;
  // Customizable Area End
}

interface SS {
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

export default class IncomingCallController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  timer: number | null = null;
  private callRingAudio: HTMLAudioElement | null = null;
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.receive = this.receive.bind(this);

    this.subScribedMessages = [
      // Customizable Area Start
      getName(MessageEnum.SessionResponseMessage),
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.NavigationPayLoadMessage),
      // Customizable Area End
    ];

    this.state = {
      // Customizable Area Start
      isFullScreen: false,
      incomingCall: null,
      activeCallSession: null,
      isVideoCall: false,
      callerInfo: {
        name: "",
        avatar: "",
      },
      isMuted: false,
      isVideoOff: false,
      callStatus: "",
      callTimer: 0,
      isStatusError: false,
      isCallStarted: false,
      // Customizable Area End
    };
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  async componentDidMount(): Promise<void> {
    this.callRingAudio = new Audio();
    this.callRingAudio.src = IncomingCallSound
    this.callRingAudio.load();
    this.callRingAudio.loop = true;

    document.addEventListener("click", this.unlockAudioPlayback, { once: true })
    this.incomingCallListener();
  }

  async componentWillUnmount(): Promise<void> {
    CometChat.removeCallListener(COMETCHAT_LISTENER_ID);
    this.resetCall()

    if (this.callRingAudio) {
      this.callRingAudio.src = "";
      this.callRingAudio = null;
    }
  }

  unlockAudioPlayback = async () => {
    try {
      if (this.callRingAudio) {
        const audio = this.callRingAudio;
        if (audio) {
          audio.muted = true;
          
          await audio.play();
  
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        }
      }
    } catch (err) {}
  }  

  resetCall = () => {
    try {
      this.setState({
        activeCallSession: null,
        callerInfo: {
          avatar: "",
          name: "",
        },
        incomingCall: null,
        isCallStarted: false,
        isFullScreen: false,
        isMuted: false,
        isVideoCall: false,
        isVideoOff: false,
        callStatus: "",
        isStatusError: false,
      });
      this.pauseCallRingtone()
      this.stopTimer();
      CometChatCalls.endSession();
      CometChat.clearActiveCall();
      this.removeOngoingCallListener();
    } catch (err) {}
  };

  startTimer = () => {
    this.timer = window.setInterval(() => {
      this.setState((prevState) => ({
        callTimer: prevState.callTimer + 1,
      }));
    }, 1000);
  };

  stopTimer = () => {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.setState({ callTimer: 0 });
  };

  toggleFullScreen = () => {
    this.setState({ isFullScreen: !this.state.isFullScreen });
  };

  ongoingCallListener = () => {
    CometChatCalls.addCallEventListener(
      COMETCHAT_CALL_LISTENER_ID,
      new CometChatCalls.OngoingCallListener({
        onCallEnded: () => {
          if (this.state.incomingCall) {
            this.resetCall();
          }
        },
      })
    );
  };

  incomingCallListener = () => {
    CometChat.addCallListener(
      COMETCHAT_LISTENER_ID,
      new CometChat.CallListener({
        onIncomingCallReceived: this.handleIncomingCallReceived,
        onIncomingCallCancelled: this.resetCall,
        onCallEndedMessageReceived: this.sendCallEvent,
      })
    );
  };

  removeOngoingCallListener = () => {
    CometChatCalls.removeCallEventListener(COMETCHAT_CALL_LISTENER_ID);
  };

  playCallRingtone = async () => {
    try {
      if (this.callRingAudio) {
        await this.callRingAudio.play();
      }
    } catch (err) {}
  };

  pauseCallRingtone = () => {
    try {
      if (this.callRingAudio) {
        this.callRingAudio.pause();
        this.callRingAudio.currentTime = 0;
      }
    } catch (err) {}
  };

  handleIncomingCallReceived = async (call: CometCall) => {
    const { isCallStarted } = this.state;

    if (isCallStarted) {
      const rejectedCall = ((await CometChat.rejectCall(
        call.sessionId,
        CometChat.CALL_STATUS.BUSY
      )) as unknown) as CometCall;
      this.sendCallEvent(rejectedCall);
      return;
    }

    this.playCallRingtone()
    const caller = call.callInitiator;

    const name = caller.name;
    const avatar = caller.avatar || "";
    this.setState({
      incomingCall: call,
      callerInfo: {
        name,
        avatar,
      },
      callStatus: CALL_TYPES[call.type],
      isVideoCall: call.type === CometChat.CALL_TYPE.VIDEO,
    });
  };

  acceptCall = async () => {
    const { incomingCall } = this.state;

    try {
      if (!incomingCall) return;
      this.pauseCallRingtone()
      const activeCall = ((await CometChat.acceptCall(
        incomingCall.sessionId
      )) as unknown) as CometCall;

      const isAudioCall = incomingCall.type === "audio";

      const callSettings = new CometChatCalls.CallSettingsBuilder()
        .enableDefaultLayout(false)
        .setIsAudioOnlyCall(isAudioCall)
        .showVirtualBackgroundSetting(false)
        .showModeButton(false)
        .build();

      const loggedInUser = await CometChat.getLoggedinUser();
      if (loggedInUser) {
        const authToken = loggedInUser.getAuthToken();

        const callResponse = await CometChatCalls.generateToken(
          incomingCall.sessionId,
          authToken
        );
        const callContainer = document.getElementById(
          "callContainer"
        ) as HTMLDivElement;
        await CometChatCalls.startSession(
          callResponse.token,
          callSettings,
          callContainer
        );
        this.ongoingCallListener();
        this.setState({
          isCallStarted: true,
          activeCallSession: activeCall,
        });
        this.sendCallEvent(activeCall);
        this.startTimer();
      }
    } catch (error) {}
  };

  endCall = async () => {
    const { incomingCall } = this.state;
    if (incomingCall) {
      const endedCall = ((await CometChat.endCall(
        incomingCall.sessionId
      )) as unknown) as CometCall;
      this.sendCallEvent(endedCall);
      this.resetCall();
    }
  };

  rejectCall = async () => {
    const { incomingCall } = this.state;
    if (incomingCall) {
      const rejectedCall = await CometChat.rejectCall(
        incomingCall.sessionId,
        CometChat.CALL_STATUS.REJECTED
      ) as unknown as CometCall;
      this.sendCallEvent(rejectedCall);
      this.resetCall();
    }
  };

  handleCallEnd = () => {
    const { isCallStarted } = this.state;
    try {
      if (isCallStarted) {
        this.endCall();
      } else {
        this.rejectCall();
      }
    } catch (err) {}
  };

  sendCallEvent = (call: CometCall) => {
    emitter.emit("incoming-call", { call });
  };

  toggleVideo = () => {
    const { isVideoOff } = this.state;
    const newVideoState = !isVideoOff;
    CometChatCalls.pauseVideo(newVideoState);
    this.setState({ isVideoOff: newVideoState });
  };

  toggleMic = () => {
    const { isMuted } = this.state;
    const newMutedState = !isMuted;
    try {
      CometChatCalls.muteAudio(newMutedState);
      this.setState({ isMuted: newMutedState });
    } catch (error) {}
  };
  // Customizable Area End
}
